<script setup lang="ts">
import InfiniteLoading from 'v3-infinite-loading'
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'

import NotificationListItem from './NotificationListItem.vue'

import { type NotificationType, RequestStatus } from '@/lib/types/notifications.types'
import { BoxComponent, FlexComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{ items: NotificationType[]; hasMore: boolean; status: RequestStatus }>()
const emit = defineEmits<{
  fetch: [reset: boolean]
  toggleStatus: [id: string, read: boolean]
  itemClick: [item: NotificationType]
}>()

const loadMore = (event: { complete: () => void; error: () => void; loaded: () => void; loading: () => void }) => {
  switch (props.status) {
    case RequestStatus.Error:
      event.error()
      break
    case RequestStatus.Loading:
      event.loading()
      break
    case RequestStatus.Success:
    default:
      emit('fetch', false)
      if (props.hasMore) {
        event.loading()
      } else {
        event.complete()
      }
      break
  }
}
</script>
<template>
  <div class="notification-list">
    <DynamicScroller :items="items" :min-item-size="54" class="notification-list__scroller">
      <template #default="{ item, index, active }">
        <DynamicScrollerItem :item="item" :active="active" :size-dependencies="[item.read]" :data-index="index">
          <NotificationListItem
            :notification="item"
            @toggle-status="emit('toggleStatus', item.id, !item.read)"
            @click="emit('itemClick', item)"
          />
        </DynamicScrollerItem>
      </template>
      <template #after>
        <InfiniteLoading :identifier="status" @infinite="loadMore">
          <template #spinner>
            <BoxComponent :p="40">
              <LoadingComponent :size="48" />
            </BoxComponent>
          </template>
          <template #complete>
            <BoxComponent :pt="40" :pb="40">
              <template v-if="items.length">
                <FlexComponent direction="column" align="center" :gap="8">
                  <TypographyComponent variant="headline-medium" text-align="center">🌈</TypographyComponent>
                  <TypographyComponent variant="title-medium" text-align="center">
                    That's all your notifications from the last 30 days.
                  </TypographyComponent>
                </FlexComponent>
              </template>
              <template v-else>
                <TypographyComponent variant="title-medium" text-align="center">
                  No new notifications yet. Check back later!
                </TypographyComponent>
              </template>
            </BoxComponent>
          </template>
          <template #error>
            <BoxComponent :pt="40" :pb="40">
              <TypographyComponent variant="title-medium" text-align="center">
                Something went wrong. Please try again later.
              </TypographyComponent>
            </BoxComponent>
          </template>
        </InfiniteLoading>
      </template>
    </DynamicScroller>
  </div>
</template>
<style lang="scss" scoped>
.notification-list {
  position: relative;
  overflow: auto;
  height: 100%;
}
.notification-list__scroller {
  height: 100%;
}
</style>
